// Per-organisation theming for the tenant app. The branding document is delivered
// top-level by GET /v1/me (tenant-wide: root org + every sub-company share one
// theme), and by the unauthenticated GET /v1/public/branding for the pre-login
// screens, which have no Cognito credentials to sign /v1/me with. Both return the
// same document; it is applied by overriding CSS custom properties on <html>:
//   - colors.primary derives the whole --color-forest-* scale (sidebar, links,
//     active states — every forest-* Tailwind utility re-skins automatically).
//   - the other tokens map 1:1 to the semantic --brand-* vars in index.css.
//   - favicon_url swaps the <link rel="icon"> set shipped by index.html.
// Unset tokens fall back to the :root defaults (the default Heymakr look) by
// removing the inline property. An empty document = default look.
//
// Duplicated per-codebase (no shared package) — matches OrgBranding in the backends.

import { publicGet } from './api';

export interface OrgBrandingColors {
  primary?: string;
  accent?: string;
  accent_text?: string;
  background?: string;
  surface?: string;
  text?: string;
  subtext?: string;
}

export interface OrgBranding {
  logo_url?: string;
  favicon_url?: string;
  app_name?: string;
  colors?: OrgBrandingColors;
}

const CACHE_KEY = 'heymakr_branding';

const FOREST_STOPS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950] as const;

// Mix ratios that turn one base hex into a 50→950 scale: lighten toward white for
// 50–500, the base itself at 600, darken toward black for 700–950. Chosen to track
// the shape of the default forest palette.
const SCALE_MIX: Record<number, { target: 'white' | 'black'; amount: number }> = {
  50: { target: 'white', amount: 0.92 },
  100: { target: 'white', amount: 0.84 },
  200: { target: 'white', amount: 0.70 },
  300: { target: 'white', amount: 0.52 },
  400: { target: 'white', amount: 0.30 },
  500: { target: 'white', amount: 0.14 },
  600: { target: 'white', amount: 0.0 },
  700: { target: 'black', amount: 0.18 },
  800: { target: 'black', amount: 0.32 },
  900: { target: 'black', amount: 0.46 },
  950: { target: 'black', amount: 0.70 },
};

interface Rgb { r: number; g: number; b: number; }

function parseHex(hex: string): Rgb | null {
  const m = /^#([0-9a-fA-F]{6})$/.exec(hex.trim());
  if (!m) return null;
  const int = parseInt(m[1], 16);
  return { r: (int >> 16) & 255, g: (int >> 8) & 255, b: int & 255 };
}

function mix(base: Rgb, target: Rgb, amount: number): Rgb {
  const t = Math.max(0, Math.min(1, amount));
  return {
    r: Math.round(base.r * (1 - t) + target.r * t),
    g: Math.round(base.g * (1 - t) + target.g * t),
    b: Math.round(base.b * (1 - t) + target.b * t),
  };
}

function rgbTriplet(colour: Rgb): string {
  return `${colour.r} ${colour.g} ${colour.b}`;
}

function mixedTriplet(base: Rgb, target: Rgb, amount: number): string {
  return rgbTriplet(mix(base, target, amount));
}

// Deterministic 50→950 forest scale from a single primary hex, returned as
// space-separated "r g b" triplets for the rgb(var() / <alpha>) Tailwind pattern.
export function deriveForestScale(primaryHex: string): Record<number, string> | null {
  const base = parseHex(primaryHex);
  if (!base) return null;
  const white: Rgb = { r: 255, g: 255, b: 255 };
  const black: Rgb = { r: 0, g: 0, b: 0 };
  const out: Record<number, string> = {};
  for (const stop of FOREST_STOPS) {
    const spec = SCALE_MIX[stop];
    const c = spec.amount === 0 ? base : mix(base, spec.target === 'white' ? white : black, spec.amount);
    out[stop] = `${c.r} ${c.g} ${c.b}`;
  }
  return out;
}

const SEMANTIC_VARS: Array<{ cssVar: string; key: keyof OrgBrandingColors }> = [
  { cssVar: '--brand-cta', key: 'accent' },
  { cssVar: '--brand-cta-text', key: 'accent_text' },
  { cssVar: '--brand-page-bg', key: 'background' },
  { cssVar: '--brand-surface', key: 'surface' },
  { cssVar: '--brand-text', key: 'text' },
  { cssVar: '--brand-subtext', key: 'subtext' },
];

// The browser tab icon. index.html ships the default marks as <link rel="icon"> and
// apple-touch-icon tags; a branded favicon detaches that whole set and replaces it with one
// link per rel, and clearing the branding re-attaches the originals. The defaults are
// captured from the DOM the first time an override lands rather than rebuilt from a
// hardcoded path, so this module never has to know which files index.html points at.
const ICON_LINK_SELECTOR = 'link[rel~="icon"], link[rel="apple-touch-icon"]';
const BRANDED_ICON_ATTR = 'data-brand-icon';

let defaultIconLinks: HTMLLinkElement[] | null = null;

// The tab icon is fetched by URL, so the browser wants a type hint for the formats it does
// not sniff reliably. The CDN key always ends in the real extension, before the ?v= buster.
const ICON_MIME_BY_EXT: Record<string, string> = {
  png: 'image/png',
  svg: 'image/svg+xml',
  ico: 'image/x-icon',
};

function iconMimeFor(url: string): string | undefined {
  const path = url.split(/[?#]/)[0];
  return ICON_MIME_BY_EXT[path.slice(path.lastIndexOf('.') + 1).toLowerCase()];
}

// Swaps in the tenant favicon, or restores the shipped one when passed null/undefined.
// A no-op when the DOM already sits in the requested state: applyBranding runs several
// times per page load (cache, then the public route, then /v1/me), and re-attaching an
// icon link makes the browser refetch it.
export function applyFavicon(url: string | null | undefined): void {
  if (typeof document === 'undefined') return;
  const head = document.head;
  if (!head) return;

  const branded = Array.from(document.querySelectorAll<HTMLLinkElement>(`link[${BRANDED_ICON_ATTR}]`));
  // getAttribute, not .href: the property resolves against the page origin, which would
  // never compare equal to the absolute CDN URL we were handed.
  if ((url ?? null) === (branded[0]?.getAttribute('href') ?? null)) return;

  if (defaultIconLinks === null) {
    defaultIconLinks = Array.from(document.querySelectorAll<HTMLLinkElement>(ICON_LINK_SELECTOR));
  }
  for (const link of branded) link.remove();

  if (!url) {
    for (const link of defaultIconLinks) if (!link.isConnected) head.appendChild(link);
    return;
  }

  for (const link of defaultIconLinks) link.remove();
  const type = iconMimeFor(url);
  for (const rel of ['icon', 'apple-touch-icon']) {
    const link = document.createElement('link');
    link.setAttribute(BRANDED_ICON_ATTR, '');
    link.rel = rel;
    link.href = url;
    // `sizes` is deliberately omitted: one uploaded file serves every size, and advertising
    // sizes it does not have makes the browser pick between marks that do not exist.
    if (type) link.type = type;
    head.appendChild(link);
  }
}

// Applies (or clears) the branding by setting/removing CSS custom properties on
// <html> plus the tab icon. Passing null/empty resets everything to the defaults.
export function applyBranding(branding: OrgBranding | null | undefined): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;

  const clearForest = () => FOREST_STOPS.forEach(s => root.style.removeProperty(`--color-forest-${s}`));
  const colors = branding?.colors ?? {};

  const scale = colors.primary ? deriveForestScale(colors.primary) : null;
  if (scale) {
    for (const stop of FOREST_STOPS) root.style.setProperty(`--color-forest-${stop}`, scale[stop]);
  } else {
    clearForest();
  }

  for (const { cssVar, key } of SEMANTIC_VARS) {
    const value = colors[key];
    if (value) root.style.setProperty(cssVar, value);
    else root.style.removeProperty(cssVar);
  }

  const appTokenMap: Array<{ cssVar: string; key: keyof OrgBrandingColors }> = [
    { cssVar: '--color-app-canvas', key: 'background' },
    { cssVar: '--color-app-surface', key: 'surface' },
    { cssVar: '--color-app-ink', key: 'text' },
    { cssVar: '--color-app-muted', key: 'subtext' },
    { cssVar: '--color-app-brand', key: 'primary' },
    { cssVar: '--color-app-accent', key: 'accent' },
    { cssVar: '--color-app-accent-text', key: 'accent_text' },
  ];

  for (const { cssVar, key } of appTokenMap) {
    const value = colors[key];
    const parsed = value ? parseHex(value) : null;
    if (parsed) root.style.setProperty(cssVar, rgbTriplet(parsed));
    else root.style.removeProperty(cssVar);
  }

  const surface = colors.surface ? parseHex(colors.surface) : null;
  const text = colors.text ? parseHex(colors.text) : null;
  if (surface && text) {
    root.style.setProperty('--color-app-surface-raised', mixedTriplet(surface, { r: 255, g: 255, b: 255 }, 0.35));
    root.style.setProperty('--color-app-line', mixedTriplet(surface, text, 0.18));
    root.style.setProperty('--color-app-line-strong', mixedTriplet(surface, text, 0.42));
  } else {
    root.style.removeProperty('--color-app-surface-raised');
    root.style.removeProperty('--color-app-line');
    root.style.removeProperty('--color-app-line-strong');
  }

  applyFavicon(branding?.favicon_url);
}

export function cacheBranding(branding: OrgBranding | null | undefined): void {
  try {
    if (branding && Object.keys(branding).length > 0) {
      localStorage.setItem(CACHE_KEY, JSON.stringify(branding));
    } else {
      localStorage.removeItem(CACHE_KEY);
    }
  } catch {
    /* localStorage unavailable — ignore */
  }
}

export function getCachedBranding(): OrgBranding | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? (JSON.parse(raw) as OrgBranding) : null;
  } catch {
    return null;
  }
}

interface PublicBrandingResponse {
  branding?: OrgBranding;
}

// Pre-auth theme. The login / forgot-password / signup screens run without Cognito
// credentials, so they cannot call GET /v1/me — this reads the same document from the
// unauthenticated route, applies it and refreshes the cache. Returns the branding so the
// caller can push it into React state (Logo and the app name read it from context), or
// null when the call fails: the cached copy (or the default look) then stands, because a
// missing theme must never keep anyone from signing in.
export async function fetchPublicBranding(): Promise<OrgBranding | null> {
  try {
    const response = await publicGet<PublicBrandingResponse>('/v1/public/branding');
    const branding = response.branding ?? {};
    applyBranding(branding);
    cacheBranding(branding);
    return branding;
  } catch {
    return null;
  }
}

// Apply any cached branding at module load so a returning user gets their theme
// before GET /v1/public/branding or /v1/me resolves, preventing a flash of the
// default Heymakr theme.
applyBranding(getCachedBranding());
